import { Button } from '@solinkcorp/solink-ui';
import classNames from 'classnames';
import {
  createTreeNodes,
  getParentTagIdsAndTarget,
  isAtleastOneChild,
} from 'components/SettingsPage/LocationManagement/locationTagUtils';
import { type TagFilter } from 'components/SettingsPage/LocationManagement/types';
import { CircleXIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import { sortLocationTagFilterCategoryToEnd } from './locationSelectorUtils';
import styles from './LocationTaggingFilters.module.sass';

interface LocationTaggingFiltersProps {
  selectedFiltersById: readonly string[];
  onSelectedFiltersByIdChange: (selectedFilters: string[]) => void;
  filters: TagFilter[];
  visibleTagsById: string[];
  disabled?: boolean;
}

export default function LocationTaggingFilters(props: LocationTaggingFiltersProps) {
  const {
    selectedFiltersById,
    onSelectedFiltersByIdChange,
    filters,
    visibleTagsById,
    disabled = false,
  } = props;

  const tagFilters = useMemo(() => createTreeNodes(filters, false), [filters]);

  const updateSelectedFilter = (id: string) => {
    const isSelected = selectedFiltersById.includes(id);
    const tagIds = getParentTagIdsAndTarget(id);
    if (!isSelected) {
      onSelectedFiltersByIdChange([...new Set([...selectedFiltersById, ...tagIds])]);
    } else {
      onSelectedFiltersByIdChange([...selectedFiltersById].filter((tagId) => !tagId.includes(id)));
    }
  };

  const filtersClass = classNames(styles['location-tagging-filters'], {
    [styles.disabled]: disabled,
  });

  return (
    <div className={filtersClass}>
      {tagFilters.map((tagFilter, index) => (
        <Tag
          disabled={disabled}
          key={tagFilter.id}
          tagFilter={tagFilter}
          updateSelectedFilter={updateSelectedFilter}
          selectedFiltersById={selectedFiltersById}
          visibleTagsById={visibleTagsById}
          index={index}
          onSelectedFiltersByIdChange={onSelectedFiltersByIdChange}
        />
      ))}
    </div>
  );
}

function Tag({
  disabled = false,
  tagFilter,
  updateSelectedFilter,
  selectedFiltersById,
  visibleTagsById,
  index,
  onSelectedFiltersByIdChange,
}: {
  disabled?: boolean;
  tagFilter: TagFilter;
  updateSelectedFilter: (id: string) => void;
  selectedFiltersById: readonly string[];
  visibleTagsById: readonly string[];
  index: number;
  onSelectedFiltersByIdChange: LocationTaggingFiltersProps['onSelectedFiltersByIdChange'];
}) {
  const displayChildren =
    tagFilter.children && isAtleastOneChild(tagFilter.children, visibleTagsById);
  const locationLabelAndTagsClass = classNames({
    [styles.locationLabelAndTagsIndentation]: tagFilter.depth,
  });
  const filterButtonClass = classNames(styles.filterButton, {
    [styles.selected]: selectedFiltersById.includes(tagFilter.id),
  });
  const labelWrapperClass = classNames(styles.labelWrapper, {
    [styles.first]: index === 0,
  });
  const locale = useLocale();

  const sortedChildrenTagFilters = useMemo(
    () => [...(tagFilter?.children || [])].sort(sortLocationTagFilterCategoryToEnd),
    [tagFilter],
  );
  return (
    <React.Fragment key={tagFilter.id}>
      {tagFilter.type === 'tag' && visibleTagsById.includes(tagFilter.id) && (
        <button
          type="button"
          className={filterButtonClass}
          onClick={() => updateSelectedFilter(tagFilter.id)}
          disabled={disabled}
        >
          {tagFilter.label}
        </button>
      )}
      {tagFilter.type === 'category' && displayChildren && (
        <div className={locationLabelAndTagsClass}>
          <div className={labelWrapperClass}>
            <label>{tagFilter.label}</label>
            {index === 0 && !!selectedFiltersById.length && (
              <Button
                type="button"
                variant="ghost"
                disabled={disabled}
                title="clear"
                className={styles.clearButton}
                onClick={() => onSelectedFiltersByIdChange([])}
              >
                <CircleXIcon className="size-4" />
                {locale.locationSelector.clearFilters}
              </Button>
            )}
          </div>
          {tagFilter.children &&
            sortedChildrenTagFilters.map((filter, index) => (
              <Tag
                disabled={disabled}
                key={filter.id}
                tagFilter={filter}
                updateSelectedFilter={updateSelectedFilter}
                selectedFiltersById={selectedFiltersById}
                visibleTagsById={visibleTagsById}
                index={index}
                onSelectedFiltersByIdChange={onSelectedFiltersByIdChange}
              />
            ))}
        </div>
      )}
    </React.Fragment>
  );
}
